/* RA US -> DE PDP purchase adapter (FoxEcom Sleek 1.7)
 * Variant metafield custom.preorder_enabled + PDP Buy Buttons enable_ra_preorder.
 * Leaves native ProductForm, Shopify payment_button, and Sticky ATC implementation intact.
 * Direct CHECK OUT intentionally bypasses the pre-order informational modal.
 */
(() => {
  'use strict';
  if (window.__raDePurchaseAdapter) {
    window.__raDePurchaseAdapter.init();
    return;
  }

  const FALLBACK_TITLE = 'Pre-Order Information';
  const FALLBACK_CONFIRM = 'CONFIRM & CONTINUE SHOPPING';
  const FALLBACK_HTML = '<div class="ra-preorder__copy rte"><p>This item is available for pre-order. Please confirm before adding it to your cart.</p></div>';
  const controllers = new Map();
  let frame = 0;

  function splitTargets(raw) {
    return (raw || '').split(/[\n,;]+/).map((value) => value.trim().toLowerCase()).filter(Boolean);
  }

  function getMainForm(sectionId) {
    return document.getElementById(`product-form-${sectionId}`);
  }

  function getMainProductInfo(sectionId) {
    return document.getElementById(`MainProduct-${sectionId}`);
  }

  function getStickyForm(productId) {
    const sticky = document.querySelector('#shopify-section-sticky-atc-bar sticky-atc-bar');
    if (!sticky || String(sticky.dataset.productId) !== String(productId)) return null;
    return sticky.querySelector('form[is="product-form"]');
  }

  function variantId(form) {
    return String(form?.querySelector('[name="id"]')?.value || '');
  }

  function formButton(form) {
    return form?.querySelector('button[name="add"]') || null;
  }

  class Controller {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.ownerSection;
      this.productId = String(root.dataset.productId);
      this.nativeAtcEnabled = root.dataset.nativeAtc !== 'false';
      this.modal = root.querySelector('[data-ra-preorder-modal]');
      this.panel = root.querySelector('[data-ra-preorder-panel]');
      this.heading = root.querySelector('[data-ra-preorder-heading]');
      this.body = root.querySelector('[data-ra-preorder-body]');
      this.confirm = root.querySelector('[data-ra-preorder-confirm]');
      this.variants = new Map();
      this.copies = Array.from(root.querySelectorAll('template[data-ra-preorder-copy]'));
      this.events = new AbortController();
      this.opened = null;
      this.pendingVariant = '';
      this.busy = false;
      this.allowedSubmit = null;
      this.originalOverflow = null;
      this.originalPadding = null;
      this.hideTimer = 0;
      this.busyTimer = 0;
      this.stickyButton = null;

      try {
        const data = JSON.parse(root.querySelector('[data-ra-preorder-variants]')?.textContent || '[]');
        data.forEach((variant) => this.variants.set(String(variant.id), variant));
      } catch (error) {
        console.warn('RA DE Pre-Order: variant metadata could not be parsed.', error);
      }

      const signal = this.events.signal;
      root.querySelector('[data-ra-preorder-close]')?.addEventListener('click', () => this.close(), { signal });
      this.confirm?.addEventListener('click', () => this.addToCart(), { signal });
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.close();
      }, { signal });
      document.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
      this.sync();
    }

    getVariant(form) {
      return this.variants.get(variantId(form)) || null;
    }

    isPreorder(variant) {
      return this.nativeAtcEnabled && variant?.preorder === true;
    }

    getMainForm() {
      return getMainForm(this.sectionId);
    }

    getStickyForm() {
      return getStickyForm(this.productId);
    }

    ownsForm(form) {
      return form === this.getMainForm() || form === this.getStickyForm();
    }

    ensureStickyTrigger(form) {
      if (!form) return null;
      const native = formButton(form);
      if (!native) return null;
      let trigger = form.querySelector('[data-ra-preorder-trigger="sticky"]');
      if (!trigger) {
        trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = native.className + ' ra-preorder__trigger ra-preorder__sticky-trigger';
        trigger.dataset.raPreorderTrigger = 'sticky';
        trigger.dataset.raPreorderOwner = this.sectionId;
        trigger.textContent = this.root.dataset.preorderButtonLabel || 'Pre-Order';
        trigger.hidden = true;
        native.insertAdjacentElement('afterend', trigger);
      }
      this.stickyButton = trigger;
      return trigger;
    }

    syncForm(form, mode) {
      if (!form) return;
      const native = formButton(form);
      if (!native) return;
      const trigger = mode === 'sticky'
        ? this.ensureStickyTrigger(form)
        : form.querySelector('[data-ra-preorder-trigger="main"]');
      if (!trigger && mode === 'main') return;
      const variant = this.getVariant(form);
      const active = this.isPreorder(variant);
      const available = Boolean(variant?.available) && !native.disabled && native.getAttribute('aria-disabled') !== 'true';
      const nativeVisible = (mode === 'sticky' || this.nativeAtcEnabled) && !active;
      native.hidden = !nativeVisible;
      if (trigger) {
        trigger.hidden = !active;
        trigger.disabled = !available || this.busy;
      }
    }

    sync() {
      this.syncForm(this.getMainForm(), 'main');
      this.syncForm(this.getStickyForm(), 'sticky');
    }

    resolveCopy(variant) {
      const id = String(variant.id).toLowerCase();
      const sku = String(variant.sku || '').trim().toLowerCase();
      const specific = this.copies.find((copy) => {
        const targets = splitTargets(copy.dataset.targets);
        return targets.length && (targets.includes(id) || (sku && targets.includes(sku)));
      });
      return specific || this.copies.find((copy) => !splitTargets(copy.dataset.targets).length) || null;
    }

    putCopy(variant) {
      const template = this.resolveCopy(variant);
      if (this.heading) this.heading.textContent = template?.dataset.heading?.trim() || FALLBACK_TITLE;
      if (this.body) this.body.innerHTML = template?.innerHTML.trim() || FALLBACK_HTML;
      if (this.confirm) this.confirm.textContent = template?.dataset.confirmLabel?.trim() || FALLBACK_CONFIRM;
    }

    lockBody() {
      if (this.originalOverflow !== null) return;
      this.originalOverflow = document.body.style.overflow;
      this.originalPadding = document.body.style.paddingRight;
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
    }

    unlockBody() {
      if (this.originalOverflow === null) return;
      document.body.style.overflow = this.originalOverflow;
      document.body.style.paddingRight = this.originalPadding;
      this.originalOverflow = null;
      this.originalPadding = null;
    }

    open(trigger) {
      if (!this.modal || !this.modal.hidden || this.busy) return;
      const form = trigger?.closest('form');
      const variant = this.getVariant(form);
      if (!this.ownsForm(form) || !this.isPreorder(variant) || !variant?.available || formButton(form)?.disabled) return;
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
      this.opened = { form, id: String(variant.id), trigger };
      this.putCopy(variant);
      window.clearTimeout(this.hideTimer);
      this.modal.hidden = false;
      this.lockBody();
      window.requestAnimationFrame(() => {
        this.modal.classList.add('is-open');
        this.panel?.focus({ preventScroll: true });
      });
    }

    close(restoreFocus = true) {
      if (!this.modal || this.modal.hidden) return;
      const opener = this.opened?.trigger;
      this.modal.classList.remove('is-open');
      this.unlockBody();
      this.opened = null;
      window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => {
        this.modal.hidden = true;
        if (restoreFocus && opener?.isConnected && !opener.hidden) opener.focus({ preventScroll: true });
      }, 180);
    }

    addToCart() {
      if (this.busy || !this.opened) return;
      const { form, id } = this.opened;
      const native = formButton(form);
      const variant = this.getVariant(form);
      if (!native?.isConnected || native.disabled || native.getAttribute('aria-disabled') === 'true' ||
          !variant?.available || String(variant.id) !== id || !this.isPreorder(variant)) {
        this.close();
        scheduleSync();
        return;
      }
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

      this.busy = true;
      this.pendingVariant = id;
      this.close(false);
      scheduleSync();
      window.clearTimeout(this.busyTimer);
      this.busyTimer = window.setTimeout(() => this.releaseBusy(), 30000);
      this.allowedSubmit = form;
      try {
        if (typeof form.requestSubmit === 'function') form.requestSubmit(native);
        else native.click();
      } catch (error) {
        console.error('RA DE Pre-Order: native form submission failed.', error);
        this.releaseBusy();
      } finally {
        this.allowedSubmit = null;
      }
    }

    releaseBusy() {
      window.clearTimeout(this.busyTimer);
      this.busyTimer = 0;
      this.busy = false;
      this.pendingVariant = '';
      scheduleSync();
    }

    onKeyDown(event) {
      if (!this.modal || this.modal.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = Array.from(this.modal.querySelectorAll('button:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'))
        .filter((node) => !node.hidden && node.getClientRects().length);
      if (!nodes.length) {
        event.preventDefault();
        this.panel?.focus({ preventScroll: true });
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (document.activeElement === this.panel) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    destroy() {
      this.events.abort();
      window.clearTimeout(this.hideTimer);
      window.clearTimeout(this.busyTimer);
      this.unlockBody();
      // The adapter-created button is NOT part of the original Sleek Sticky ATC file.
      if (this.stickyButton?.isConnected) this.stickyButton.remove();
      // Restore the sticky native button when a different product is swapped in.
      const sticky = this.getStickyForm();
      if (sticky) formButton(sticky)?.removeAttribute('hidden');
    }
  }

  function scheduleSync() {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      init();
      controllers.forEach((controller) => controller.sync());
      syncDirectCheckoutButtons();
    });
  }

  function syncDirectCheckoutButtons() {
    document.querySelectorAll('[data-ra-direct-checkout]').forEach((button) => {
      if (button.getAttribute('aria-busy') === 'true') return;
      const form = button.closest('form');
      const native = formButton(form);
      if (native) button.disabled = native.disabled || native.getAttribute('aria-disabled') === 'true';
    });
  }

  function init() {
    // During Sleek view transitions both old and new <main> can coexist briefly.
    // Sleek inserts the new subtree before the old one; keep the first root per section.
    const firstRoots = new Map();
    document.querySelectorAll('[data-ra-preorder-root]').forEach((root) => {
      if (!firstRoots.has(root.dataset.ownerSection)) firstRoots.set(root.dataset.ownerSection, root);
    });
    const roots = Array.from(firstRoots.values());
    const active = new Set(roots);
    for (const [sectionId, controller] of controllers) {
      if (!active.has(controller.root) || !controller.root.isConnected) {
        controller.destroy();
        controllers.delete(sectionId);
      }
    }
    roots.forEach((root) => {
      const sectionId = root.dataset.ownerSection;
      const old = controllers.get(sectionId);
      if (old?.root === root) return;
      if (old) old.destroy();
      controllers.set(sectionId, new Controller(root));
    });
    syncDirectCheckoutButtons();
  }

  function ownerOf(form) {
    for (const controller of controllers.values()) {
      if (controller.ownsForm(form)) return controller;
    }
    return null;
  }

  function showFormError(form, message) {
    const field = form?.querySelector('.product-form__error-message');
    if (field) {
      field.textContent = message;
      field.hidden = false;
    } else if (message) {
      window.alert(message);
    }
  }

  async function directCheckout(button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const form = button.closest('form');
    const native = formButton(form);
    if (!form || !native || native.disabled || native.getAttribute('aria-disabled') === 'true') return;
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
    const data = new FormData(form);
    if (!data.get('id')) return;
    const errorField = form.querySelector('.product-form__error-message');
    if (errorField) errorField.hidden = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const route = window.FoxTheme?.routes?.cart_add_url || '/cart/add';
      const url = route.endsWith('.js') ? route : `${route}.js`;
      const response = await fetch(url, {
        method: 'POST',
        body: data,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const json = await response.json();
      if (!response.ok || json.status) {
        throw new Error(json.description || json.message || 'Unable to add this item to your cart. Please try again.');
      }
      window.location.assign('/checkout');
    } catch (error) {
      showFormError(form, error?.message || 'Unable to proceed to checkout. Please try again.');
      button.disabled = false;
      button.removeAttribute('aria-busy');
      scheduleSync();
    }
  }

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest('[data-ra-direct-checkout]');
    if (checkout) {
      event.preventDefault();
      if (!checkout.disabled) directCheckout(checkout);
      return;
    }
    const trigger = event.target.closest('[data-ra-preorder-trigger]');
    if (!trigger) return;
    const controller = controllers.get(trigger.dataset.raPreorderOwner);
    if (!controller || !controller.ownsForm(trigger.closest('form'))) return;
    event.preventDefault();
    controller.open(trigger);
  });

  // Capture submit BEFORE Sleek's own ProductForm handler. Checkout does not submit the form.
  document.addEventListener('submit', (event) => {
    const controller = ownerOf(event.target);
    if (!controller || controller.allowedSubmit === event.target) return;
    const variant = controller.getVariant(event.target);
    if (!controller.isPreorder(variant) || !variant?.available) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const mode = event.target === controller.getMainForm() ? 'main' : 'sticky';
    const trigger = event.target.querySelector(`[data-ra-preorder-trigger="${mode}"]`);
    if (trigger) controller.open(trigger);
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target.closest('product-info, sticky-atc-bar')) scheduleSync();
  });

  // Native DE variantChange is more reliable than a DOM event for async section-rendered variants.
  function subscribeToTheme() {
    if (!window.FoxTheme?.pubsub) return;
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const product = getMainProductInfo(data.sectionId);
      if (!product) return; // ignore quick-view / unrelated product forms
      const form = getMainForm(data.sectionId);
      const checkout = form?.querySelector('[data-ra-direct-checkout]');
      if (checkout && checkout.getAttribute('aria-busy') !== 'true') {
        checkout.disabled = !data.variant?.available || Boolean(formButton(form)?.disabled);
      }
      scheduleSync();
    });
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, (data) => {
      controllers.forEach((controller) => {
        if (controller.pendingVariant && String(data.productVariantId) === controller.pendingVariant) controller.releaseBusy();
      });
    });
  }

  document.addEventListener('product-ajax:added', (event) => {
    const addedId = String(event.detail?.product?.id || '');
    controllers.forEach((controller) => {
      if (controller.pendingVariant && addedId === controller.pendingVariant) controller.releaseBusy();
    });
  });
  document.addEventListener('product-ajax:error', () => {
    // Errors also publish FoxTheme cartError, with the variant ID, so avoid cross-form releases here.
  });

  document.addEventListener('shopify:section:load', scheduleSync);
  document.addEventListener('shopify:section:unload', scheduleSync);

  // Sleek swaps <main> and the separate sticky bar during product URL option transitions.
  function watchSwaps() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('[data-ra-preorder-root], product-info, sticky-atc-bar, [data-ra-direct-checkout]') ||
              node.querySelector?.('[data-ra-preorder-root], product-info, sticky-atc-bar, [data-ra-direct-checkout]')) {
            scheduleSync();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  const api = { init };
  window.__raDePurchaseAdapter = api;
  const start = () => {
    init();
    subscribeToTheme();
    watchSwaps();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
